/**
 * MSAL Cache Manager
 * Automatically clears MSAL cache after successful authentication
 * since we manage tokens in our unified auth service
 */

import { msalInstance } from './authConfig';
import { logger } from '@/common/services/config';

class MSALCacheManager {
  private readonly MSAL_KEYS_PREFIX = 'msal.';
  private cleanupTimer: NodeJS.Timeout | null = null;

  /**
   * Clear all MSAL cache from storage
   */
  clearMSALCache(): void {
    try {
      // Clear MSAL instance cache
      msalInstance.clearCache();

      // Clear any remaining MSAL entries from localStorage
      this.clearMSALFromStorage(localStorage);

      // Clear any remaining MSAL entries from sessionStorage
      this.clearMSALFromStorage(sessionStorage);
    } catch (error) {
      logger.warn('Error clearing MSAL cache:', error);
    }
  }

  /**
   * Clear MSAL entries from specific storage
   */
  private clearMSALFromStorage(storage: Storage): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && this.isMSALKey(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      storage.removeItem(key);
    });
  }

  /**
   * Check if a storage key belongs to MSAL
   */
  private isMSALKey(key: string): boolean {
    return (
      key.startsWith(this.MSAL_KEYS_PREFIX) ||
      key.includes('msal') ||
      key.includes('login.windows.net') ||
      key.includes('microsoftonline.com') ||
      // Check for the specific pattern in your logs
      /^[a-f0-9-]{36}\.[a-f0-9-]{36}-/.test(key)
    );
  }

  /**
   * Schedule automatic cleanup after authentication
   */
  scheduleCleanup(delayMs: number = 5000): void {
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
    }

    this.cleanupTimer = setTimeout(() => {
      this.clearMSALCache();
      this.cleanupTimer = null;
    }, delayMs);
  }

  /**
   * Cancel scheduled cleanup
   */
  cancelCleanup(): void {
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Get current MSAL cache size (for debugging)
   */
  getMSALCacheSize(): {
    localStorage: number;
    sessionStorage: number;
    keys: string[];
  } {
    const result = {
      localStorage: 0,
      sessionStorage: 0,
      keys: [] as string[],
    };

    // Check localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && this.isMSALKey(key)) {
        result.localStorage++;
        result.keys.push(`localStorage: ${key}`);
      }
    }

    // Check sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && this.isMSALKey(key)) {
        result.sessionStorage++;
        result.keys.push(`sessionStorage: ${key}`);
      }
    }

    return result;
  }

  /**
   * Debug current auth token to check authentication state
   */
  debugAuthToken(): void {
    try {
      const authTokenData =
        localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      if (!authTokenData) {
        logger.debug('❌ No auth_token found in storage');
        return;
      }

      const authToken = JSON.parse(authTokenData);
      logger.debug('🔍 Auth Token Debug:', {
        has_access_token: !!authToken.access_token,
        expires_at: authToken.expires_at,
        user_email: authToken.user_info?.email,
        scopes_used: 'Check console logs for scope info',
      });

      logger.debug('ℹ️ REFRESH TOKEN IS NULL - This is EXPECTED for browser applications');
      logger.debug(
        '📚 Microsoft Learn: https://learn.microsoft.com/en-us/answers/questions/2238370/issue-with-refresh-token-acquisition-in-msal-node'
      );
      logger.debug(
        '🔒 Browser security prevents refresh tokens. MSAL handles token refresh automatically.'
      );
      logger.debug(
        '✅ For browser apps: Use MSAL silent token acquisition instead of refresh tokens'
      );
      logger.debug('🖥️ For desktop: MSAL manages refresh tokens internally');
      logger.debug('📱 For mobile: Refresh tokens work with native PKCE flow');
    } catch (error) {
      logger.error('❌ Error debugging auth token:', error);
    }
  }
}

export const msalCacheManager = new MSALCacheManager();

// Expose cache manager for debugging
if (typeof window !== 'undefined') {
  (window as any).debugStorage = {
    ...(window as any).debugStorage,
    msalCacheManager,
    clearMSALCache: () => msalCacheManager.clearMSALCache(),
    getMSALCacheSize: () => msalCacheManager.getMSALCacheSize(),
    debugAuthToken: () => msalCacheManager.debugAuthToken(),
  };
}
